import apiBase, { normalizeApi } from '../config/apiBase';
import { withCreds } from '../authService';
import { publishVoiceOutputEnergy } from './voiceEnergyBus';
import { advanceVoicePlaybackOffset } from './voiceTurnController';

let audioContext = null;
let activePlayback = null;
let voiceRequestVersion = 0;
let fallbackAudio = null;
let fallbackObjectUrl = null;
let playbackEnergyFrame = null;
const HTML_AUDIO_START_TIMEOUT_MS = 2500;
export const TTS_REQUEST_TIMEOUT_MS = 15000;

function ttsFailure(message, stage, cause = null) {
  const error = new Error(message);
  error.ttsStage = stage;
  if (cause) error.cause = cause;
  return error;
}

function stopPlaybackEnergyMeter() {
  if (playbackEnergyFrame && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(playbackEnergyFrame);
  }
  playbackEnergyFrame = null;
  publishVoiceOutputEnergy(0);
}

function startPlaybackEnergyMeter(playback) {
  stopPlaybackEnergyMeter();
  const analyser = playback?.analyser;
  if (!analyser || typeof requestAnimationFrame !== 'function') return;

  const data = new Uint8Array(analyser.fftSize);
  let smoothed = 0;

  const tick = () => {
    if (activePlayback !== playback || playback.stopped) {
      stopPlaybackEnergyMeter();
      return;
    }
    if (playback.paused) {
      smoothed *= 0.72;
      publishVoiceOutputEnergy(smoothed);
      playbackEnergyFrame = requestAnimationFrame(tick);
      return;
    }

    analyser.getByteTimeDomainData(data);
    let peak = 0;
    let squares = 0;
    for (let index = 0; index < data.length; index += 1) {
      const sample = (data[index] - 128) / 128;
      peak = Math.max(peak, Math.abs(sample));
      squares += sample * sample;
    }
    const rms = Math.sqrt(squares / data.length);
    const measured = Math.min(1, Math.pow(rms * 2.8 + peak * 0.72, 0.78));
    smoothed = measured > smoothed ? measured * 0.72 + smoothed * 0.28 : measured * 0.18 + smoothed * 0.82;
    publishVoiceOutputEnergy(smoothed);
    playbackEnergyFrame = requestAnimationFrame(tick);
  };

  playbackEnergyFrame = requestAnimationFrame(tick);
}

function isSafariPlaybackRuntime() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = String(navigator.userAgent || '');
  const vendor = String(navigator.vendor || '');
  return vendor === 'Apple Computer, Inc.' && /AppleWebKit\//i.test(userAgent) && /Safari\//i.test(userAgent);
}

export function isAppleMobileVoiceRuntime() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = String(navigator.userAgent || '');
  const platform = String(navigator.platform || '');
  const touchPoints = Number(navigator.maxTouchPoints || 0);
  const mobileAppleDevice = /iPhone|iPad|iPod/i.test(userAgent) ||
    (platform === 'MacIntel' && touchPoints > 1);
  return mobileAppleDevice && /AppleWebKit\//i.test(userAgent);
}

export function requiresExclusiveVoicePlayback() {
  return isSafariPlaybackRuntime();
}

// WebKit can leave the page in its quiet microphone/communications route
// after getUserMedia tracks stop.  Use the browser's own audio-session hint
// when it exists, but keep every other runtime on the existing path.
export function setBrightResolveAudioSessionType(type = 'auto') {
  if (typeof navigator === 'undefined' || !navigator.audioSession) return false;
  try {
    navigator.audioSession.type = type;
    return true;
  } catch (_) {
    return false;
  }
}

function stopPrimingAudio() {
  const element = fallbackAudio;
  if (!element || element.dataset?.brPriming !== 'true') return false;
  try {
    element.pause();
    element.loop = false;
    element.removeAttribute('src');
    element.removeAttribute?.('data-br-priming');
    element.load();
    return true;
  } catch (_) {
    return false;
  }
}

function getFallbackAudio() {
  if (typeof window === 'undefined' || typeof window.Audio !== 'function') return null;
  if (!fallbackAudio) {
    fallbackAudio = new window.Audio();
    fallbackAudio.preload = 'auto';
    fallbackAudio.playsInline = true;
    fallbackAudio.setAttribute?.('playsinline', '');
  }
  return fallbackAudio;
}

function htmlAudioReadyState(element) {
  return ({
    0: 'have_nothing',
    1: 'have_metadata',
    2: 'have_current_data',
    3: 'have_future_data',
    4: 'have_enough_data',
  })[Number(element?.readyState)] || 'unavailable';
}

function htmlAudioNetworkState(element) {
  return ({
    0: 'empty',
    1: 'idle',
    2: 'loading',
    3: 'no_source',
  })[Number(element?.networkState)] || 'unavailable';
}

function htmlAudioPositionState(element) {
  const position = Number(element?.currentTime);
  if (!Number.isFinite(position)) return 'unavailable';
  return position > 0 ? 'positive' : 'zero';
}

function reportHtmlAudioLifecycle(options, stage, element, details = {}) {
  try {
    options.onMediaLifecycle?.({
      stage,
      readyState: htmlAudioReadyState(element),
      networkState: htmlAudioNetworkState(element),
      positionState: htmlAudioPositionState(element),
      attempt: Number(details.attempt || 0),
      sourcePhase: details.sourcePhase || 'unavailable',
    });
  } catch (_) {
    // Observability must never alter or interrupt the audio path.
  }
}

// Safari/WebKit requires an actual source to be started during a user gesture.
// Creating or merely resuming an AudioContext is not enough after an async API
// response. This one-frame silent buffer unlocks the same Web Audio pipeline
// used for ElevenLabs playback; it does not use browser-native speech voices.
export function primeBrightResolveAudio() {
  if (typeof window === 'undefined') return false;

  const AudioContext = window.AudioContext || window.webkitAudioContext;
  const htmlAudio = getFallbackAudio();
  if (!AudioContext && !htmlAudio) return false;

  try {
    if (AudioContext) {
      if (!audioContext || audioContext.state === 'closed') {
        audioContext = new AudioContext();
      }

      // Let the one-sample source finish naturally. WebKit can treat a source
      // stopped at time zero as no playback gesture at all. Re-prime on each
      // user action so microphone interruptions cannot leave the output
      // context nominally present but inaudible.
      const source = audioContext.createBufferSource();
      source.buffer = audioContext.createBuffer(1, 1, 22050);
      source.connect(audioContext.destination);
      source.start(0);
    }

    // Prime one persistent HTMLAudioElement during the same gesture. Safari
    // can later interrupt Web Audio when ownership moves from microphone to
    // speaker; the already-authorized element is the codec/output fallback.
    if (htmlAudio) {
      // The WAV itself is silent, so it can be played unmuted. Do not pause it
      // immediately: WebKit may otherwise discard the user-activation grant
      // before the asynchronous TTS response replaces this source.
      htmlAudio.muted = false;
      htmlAudio.volume = 1;
      htmlAudio.loop = true;
      htmlAudio.dataset.brPriming = 'true';
      htmlAudio.src = 'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQgAAACAgICAgICA';
      const priming = htmlAudio.play();
      if (priming?.catch) priming.catch(() => {});
    }
    return Boolean(audioContext || htmlAudio);
  } catch (_) {
    return Boolean(htmlAudio);
  }
}

export async function unlockBrightResolveAudio() {
  if (!primeBrightResolveAudio() || !audioContext) return false;

  try {
    if (audioContext.state !== 'running') {
      await audioContext.resume();
    }
    return audioContext.state === 'running';
  } catch (_) {
    return false;
  }
}

// Stops the active server-generated voice immediately.  This is used by the
// existing Stop control and by Live Voice mute/close, so a user never has to
// wait for a response to finish before speaking again.
export function stopBrightResolveVoice() {
  const playback = activePlayback;
  if (!playback) return false;

  stopPlaybackEnergyMeter();
  activePlayback = null;
  playback.source = null;
  playback.stopped = true;
  playback.onCancel?.();

  if (playback.kind === 'html-audio') {
    try {
      playback.element.pause();
      playback.element.removeAttribute('src');
      playback.element.load();
    } catch (_) {}
    if (fallbackObjectUrl) URL.revokeObjectURL(fallbackObjectUrl);
    fallbackObjectUrl = null;
    playback.resolve();
    return true;
  }

  try {
    playback.sourceBeforeStop?.stop();
    playback.resolve();
    return true;
  } catch (_) {
    playback.resolve();
    return false;
  }
}

async function playWithHtmlAudio(bytes, mimeType, requestVersion, options = {}) {
  stopPlaybackEnergyMeter();
  const element = getFallbackAudio();
  if (!element || requestVersion !== voiceRequestVersion) {
    throw ttsFailure('HTML audio playback is unavailable.', 'playback_unavailable');
  }
  if (fallbackObjectUrl) URL.revokeObjectURL(fallbackObjectUrl);
  fallbackObjectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType || 'audio/wav' }));
  const sourcePhase = options.preferSpeakerRoute ? 'after_route_handoff' : 'no_route_handoff';
  reportHtmlAudioLifecycle(options, 'html_audio_blob_created', element, { sourcePhase });
  element.loop = false;
  element.removeAttribute?.('data-br-priming');
  await new Promise((resolve, reject) => {
    let startTimer = null;
    let settled = false;
    let cancelled = false;
    let startAttempts = 0;
    let playbackStarted = false;
    let positivePositionReported = false;
    const lifecycleListeners = [];
    const observeMediaEvent = (eventName, stage) => {
      if (typeof element.addEventListener !== 'function') return;
      const listener = () => {
        if (settled) return;
        reportHtmlAudioLifecycle(options, stage, element, {
          attempt: startAttempts,
          sourcePhase,
        });
      };
      element.addEventListener(eventName, listener);
      lifecycleListeners.push([eventName, listener]);
    };
    const observeFirstPositivePosition = () => {
      if (settled || positivePositionReported || Number(element.currentTime) <= 0) return;
      positivePositionReported = true;
      reportHtmlAudioLifecycle(options, 'html_audio_first_positive_time', element, {
        attempt: startAttempts,
        sourcePhase,
      });
    };
    const clearLifecycleListeners = () => {
      if (typeof element.removeEventListener !== 'function') return;
      lifecycleListeners.forEach(([eventName, listener]) => {
        element.removeEventListener(eventName, listener);
      });
      lifecycleListeners.length = 0;
    };
    observeMediaEvent('loadstart', 'html_audio_loadstart');
    observeMediaEvent('loadedmetadata', 'html_audio_loadedmetadata');
    observeMediaEvent('loadeddata', 'html_audio_loadeddata');
    observeMediaEvent('canplay', 'html_audio_canplay');
    observeMediaEvent('canplaythrough', 'html_audio_canplaythrough');
    observeMediaEvent('playing', 'html_audio_playing');
    if (typeof element.addEventListener === 'function') {
      element.addEventListener('timeupdate', observeFirstPositivePosition);
      lifecycleListeners.push(['timeupdate', observeFirstPositivePosition]);
    }
    const clearStartTimer = () => {
      if (startTimer !== null) clearTimeout(startTimer);
      startTimer = null;
    };
    const finish = () => {
      if (settled) return;
      settled = true;
      clearStartTimer();
      clearLifecycleListeners();
      element.onended = null;
      element.onerror = null;
      if (activePlayback?.element === element) activePlayback = null;
      if (fallbackObjectUrl) URL.revokeObjectURL(fallbackObjectUrl);
      fallbackObjectUrl = null;
      if (!cancelled) options.onPlaybackComplete?.();
      resolve();
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      clearStartTimer();
      clearLifecycleListeners();
      element.onended = null;
      element.onerror = null;
      if (activePlayback?.element === element) activePlayback = null;
      try {
        element.pause();
        element.removeAttribute('src');
        element.load();
      } catch (_) {}
      if (fallbackObjectUrl) URL.revokeObjectURL(fallbackObjectUrl);
      fallbackObjectUrl = null;
      options.onPlaybackFailed?.();
      reject(ttsFailure('HTML audio playback failed.', 'playback_start'));
    };
    element.src = fallbackObjectUrl;
    reportHtmlAudioLifecycle(options, 'html_audio_src_assigned', element, { sourcePhase });
    // Preserve the existing Safari path: setting src begins media loading and
    // no explicit load() call is added by this observability checkpoint.
    reportHtmlAudioLifecycle(options, 'html_audio_load_skipped', element, { sourcePhase });
    element.autoplay = true;
    element.defaultMuted = false;
    element.muted = false;
    element.volume = 1;
    element.onended = finish;
    element.onerror = fail;
    activePlayback = {
      kind: 'html-audio', element, stopped: false, resolve: finish,
      onCancel: () => {
        cancelled = true;
        reportHtmlAudioLifecycle(options, 'html_audio_cancelled', element, {
          attempt: startAttempts,
          sourcePhase,
        });
        clearLifecycleListeners();
        options.onPlaybackCancelled?.();
      },
    };
    const markStarted = () => {
      if (settled || playbackStarted) return;
      playbackStarted = true;
      clearStartTimer();
      options.onAudioDecoded?.();
      options.onStart?.();
    };
    const attemptStart = () => {
      if (settled || playbackStarted) return;
      startAttempts += 1;
      const playbackAttempt = startAttempts;
      options.onPlaybackAttempt?.({ attempt: playbackAttempt });
      reportHtmlAudioLifecycle(options, 'html_audio_play_requested', element, {
        attempt: playbackAttempt,
        sourcePhase,
      });
      clearStartTimer();
      startTimer = setTimeout(() => {
        if (startAttempts < 2) {
          setBrightResolveAudioSessionType('playback');
          options.onPlaybackRetry?.({ attempt: startAttempts + 1 });
          attemptStart();
        } else {
          fail();
        }
      }, HTML_AUDIO_START_TIMEOUT_MS);
      try {
        const started = element.play();
        if (started?.then) {
          started.then(() => {
            reportHtmlAudioLifecycle(options, 'html_audio_play_resolved', element, {
              attempt: playbackAttempt,
              sourcePhase,
            });
            markStarted();
          }).catch(() => {
            reportHtmlAudioLifecycle(options, 'html_audio_play_rejected', element, {
              attempt: playbackAttempt,
              sourcePhase,
            });
            if (settled || playbackStarted) return;
            if (startAttempts < 2) {
              setBrightResolveAudioSessionType('playback');
              options.onPlaybackRetry?.({ attempt: startAttempts + 1 });
              attemptStart();
            } else {
              fail();
            }
          });
        } else {
          reportHtmlAudioLifecycle(options, 'html_audio_play_resolved', element, {
            attempt: playbackAttempt,
            sourcePhase,
          });
          markStarted();
        }
      } catch (_) {
        reportHtmlAudioLifecycle(options, 'html_audio_play_rejected', element, {
          attempt: playbackAttempt,
          sourcePhase,
        });
        if (startAttempts < 2) {
          setBrightResolveAudioSessionType('playback');
          options.onPlaybackRetry?.({ attempt: startAttempts + 1 });
          attemptStart();
        } else {
          fail();
        }
      }
    };
    attemptStart();
  });
}

// Invalidates both an active audio source and any TTS request that is still
// downloading.  Closing or muting Live Voice therefore cannot cause delayed
// audio to begin after the interface has returned to text chat.
export function cancelBrightResolveVoice() {
  voiceRequestVersion += 1;
  const stoppedPlayback = stopBrightResolveVoice();
  const stoppedPriming = stopPrimingAudio();
  setBrightResolveAudioSessionType('auto');
  return stoppedPlayback || stoppedPriming;
}

export function pauseBrightResolveVoice() {
  const playback = activePlayback;
  if (!playback || playback.paused) return false;

  if (playback.kind === 'html-audio') {
    try {
      playback.element.pause();
      playback.paused = true;
      return true;
    } catch (_) {
      return false;
    }
  }

  if (!playback.source) return false;

  playback.offset = advanceVoicePlaybackOffset({
    offset: playback.offset,
    startedAt: playback.startedAt,
    currentTime: audioContext.currentTime,
    duration: playback.buffer?.duration,
  });
  playback.paused = true;
  const source = playback.source;
  playback.source = null;

  try {
    source.stop();
    return true;
  } catch (_) {
    return false;
  }
}

export function resumeBrightResolveVoice() {
  const playback = activePlayback;
  if (!playback || !playback.paused) return false;

  if (playback.kind === 'html-audio') {
    playback.paused = false;
    try {
      const started = playback.element.play();
      if (started?.catch) started.catch(() => {});
      return true;
    } catch (_) {
      playback.paused = true;
      return false;
    }
  }

  if (playback.offset >= playback.buffer.duration) {
    activePlayback = null;
    playback.resolve();
    return false;
  }

  playback.paused = false;
  startPlayback(playback);
  return true;
}

function startPlayback(playback) {
  const source = audioContext.createBufferSource();
  source.buffer = playback.buffer;
  // Keep the audible ElevenLabs signal on the original direct path. The orb
  // meter receives a separate zero-gain branch so visualization can never
  // color, amplify, or otherwise alter Sonny's sound.
  source.connect(audioContext.destination);
  if (playback.analyser) {
    source.connect(playback.analyser);
    if (playback.monitorGain && !playback.monitorConnected) {
      playback.analyser.connect(playback.monitorGain);
      playback.monitorGain.connect(audioContext.destination);
      playback.monitorConnected = true;
    }
  }
  playback.source = source;
  playback.sourceBeforeStop = source;
  playback.startedAt = audioContext.currentTime;

  source.onended = () => {
    if (playback.source !== source) return;
    playback.source = null;
    if (playback.paused || playback.stopped) return;
    activePlayback = null;
    stopPlaybackEnergyMeter();
    playback.resolve();
  };

  source.start(0, playback.offset);
  startPlaybackEnergyMeter(playback);
}

// This always calls BrightResolve's server-side TTS route. The selected
// provider (ElevenLabs in production) stays private on the server.
export async function speakWithBrightResolveVoice(text, options = {}) {
  const value = String(text || '').trim();

  if (!value) return;

  const requestVersion = ++voiceRequestVersion;
  stopBrightResolveVoice();

  if (isSafariPlaybackRuntime() && options.preferSpeakerRoute) {
    const applied = setBrightResolveAudioSessionType('playback');
    options.onAudioSessionTransition?.({ state: 'playback', applied });
  }

  const configuredBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_BASE ||
    apiBase;
  // Reuse the public application's first-party cookie transport policy even
  // when a build still contains an explicit legacy Render base. Development
  // and preview hosts retain their legitimate configured API origin.
  const base = normalizeApi(String(configuredBase || '').trim());

  options.onRequest?.();

  const requestController = typeof AbortController === 'function' ? new AbortController() : null;
  let requestTimedOut = false;
  const requestTimer = requestController ? setTimeout(() => {
    requestTimedOut = true;
    requestController.abort();
  }, TTS_REQUEST_TIMEOUT_MS) : null;
  let response;
  try {
    response = await withCreds(
      `${base.replace(/\/+$/, '')}/api/tts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: value,
          latency_ref: options.latencyRef || undefined,
        }),
        signal: requestController?.signal,
      }
    );
  } catch (error) {
    throw ttsFailure(
      requestTimedOut ? 'TTS request timed out.' : 'TTS request failed.',
      requestTimedOut ? 'request_timeout' : 'request',
      error,
    );
  } finally {
    if (requestTimer !== null) clearTimeout(requestTimer);
  }

  if (!response.ok) {
    throw ttsFailure(`TTS failed: ${response.status}`, 'response');
  }
  options.onResponse?.({
    serverTiming: response.headers?.get?.('Server-Timing') || '',
    requestId: response.headers?.get?.('X-Request-ID') || '',
    buildSha: response.headers?.get?.('X-BR-Build') || '',
  });

  let bytes;
  try {
    bytes = await response.arrayBuffer();
  } catch (error) {
    throw ttsFailure('TTS audio response could not be read.', 'response_body', error);
  }
  const mimeType = response.headers?.get?.('Content-Type') || 'audio/wav';
  if (requestVersion !== voiceRequestVersion) return;
  options.onAudioReady?.();

  // Desktop Safari can report a running AudioContext yet keep its output
  // inaudible after microphone capture. The persistent media element was
  // started inside the user's voice-mode gesture and provides a reliable
  // WebKit handoff for server-generated speech.
  if (isSafariPlaybackRuntime()) {
    // iOS needs a brief, silent interval after microphone release to restore
    // the normal media/speaker route. Starting playback in the same task can
    // leave it on the quieter communications route and can make the OS audibly
    // switch audio modes. This delay happens only after TTS is downloaded and
    // only for the explicit live-voice speaker handoff.
    if (options.preferSpeakerRoute) {
      options.onRouteHandoffStart?.();
      await new Promise((resolve) => setTimeout(resolve, 180));
      if (requestVersion !== voiceRequestVersion) return;
      options.onRouteHandoffComplete?.();
    } else {
      options.onRouteHandoffStart?.();
      options.onRouteHandoffComplete?.();
    }
    await playWithHtmlAudio(bytes, mimeType, requestVersion, options);
    return;
  }

  const unlocked = await unlockBrightResolveAudio();

  if (!unlocked || !audioContext) {
    await playWithHtmlAudio(bytes, mimeType, requestVersion, options);
    return;
  }

  // Slice prevents Safari from reusing a detached ArrayBuffer while decoding.
  let decoded;
  try {
    decoded = await audioContext.decodeAudioData(bytes.slice(0));
  } catch (_) {
    await playWithHtmlAudio(bytes, mimeType, requestVersion, options);
    return;
  }
  if (requestVersion !== voiceRequestVersion) return;
  options.onAudioDecoded?.();

  // Safari may interrupt an AudioContext while the microphone or speech
  // recognizer owns the audio session. Resume again immediately before source
  // creation; the earlier gesture unlock can remain valid while the context
  // itself has transitioned away from `running` during network/decode awaits.
  if (audioContext.state !== 'running') {
    await audioContext.resume();
  }
  if (audioContext.state !== 'running') {
    await playWithHtmlAudio(bytes, mimeType, requestVersion, options);
    return;
  }

  stopPrimingAudio();

  await new Promise((resolve, reject) => {
    try {
      const playback = {
        buffer: decoded,
        offset: 0,
        paused: false,
        stopped: false,
        source: null,
        sourceBeforeStop: null,
        startedAt: 0,
        analyser: audioContext.createAnalyser?.() || null,
        monitorGain: audioContext.createGain?.() || null,
        monitorConnected: false,
        resolve,
        onCancel: options.onPlaybackCancelled,
      };
      if (playback.monitorGain) playback.monitorGain.gain.value = 0;
      if (playback.analyser) {
        playback.analyser.fftSize = 512;
        playback.analyser.smoothingTimeConstant = 0.64;
        playback.analyser.minDecibels = -92;
        playback.analyser.maxDecibels = -12;
      }
      activePlayback = playback;
      options.onPlaybackAttempt?.({ attempt: 1 });
      startPlayback(playback);
      options.onStart?.();
    } catch (error) {
      reject(ttsFailure('Decoded TTS audio could not start.', 'playback_start', error));
    }
  });
}
